import { useMemo, useRef, useState } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import AModal from '@/components/A/AModal/AModal'
import AButton from '@/components/A/AButton/AButton'
import ProjectSharePoster from '@/pages/ProjectDetailPage/components/ProjectSharePoster/ProjectSharePoster'
import styles from './SharePosterModal.module.less'
import { useTranslation } from 'react-i18next'
import copy from 'copy-to-clipboard'
import { message } from 'antd'
import html2canvas from 'html2canvas'
import { useRequest } from 'alova/client'
import { api } from '@/api'

interface SharePosterModalProps {
  project: ProjectModels.ProjectDetail
  userName: string
  userAvatar?: string | null
  downloadedAt: number
}

const waitForImagesReady = async (element: HTMLElement) => {
  const imageNodes = Array.from(element.querySelectorAll('img'))
  await Promise.all(
    imageNodes.map((img) => {
      if (img.complete) {
        return Promise.resolve()
      }
      return new Promise<void>((resolve) => {
        const done = () => resolve()
        img.addEventListener('load', done, { once: true })
        img.addEventListener('error', done, { once: true })
      })
    })
  )
}

const sanitizeFileName = (name: string) =>
  name
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60)

function SharePosterModal(props: SharePosterModalProps) {
  const modal = useModal()
  const { t } = useTranslation()
  const previewPosterRef = useRef<HTMLDivElement>(null)
  const [saving, setSaving] = useState(false)
  const { data: aigcPercentData } = useRequest(api.statistic.getAigcProjectPercentage(props.project.id), {
    initialData: {},
  })

  const percentile = useMemo(() => {
    const raw =
      (aigcPercentData as any)?.percent ??
      (aigcPercentData as any)?.percentage ??
      (aigcPercentData as any)?.percentile ??
      (aigcPercentData as any)?.aigcPercentile ??
      (aigcPercentData as any)?.ratio
    const num = Number(raw)
    if (!Number.isFinite(num)) {
      return null
    }
    const normalized = num <= 1 ? num * 100 : num
    return Math.max(0, Math.min(100, Math.round(normalized)))
  }, [aigcPercentData])

  const shareText = useMemo(
    () =>
      t('dashboard.projectDetailPage.shareModal.textTemplate', {
        score: percentile === null ? '--' : percentile,
        hostname: location.hostname,
      }),
    [t, percentile]
  )

  const handleSaveImage = async () => {
    if (!previewPosterRef.current || saving) {
      return
    }
    setSaving(true)
    try {
      await waitForImagesReady(previewPosterRef.current)
      if (document.fonts?.ready) {
        await document.fonts.ready
      }
      const canvas = await html2canvas(previewPosterRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
      })
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
      if (!blob) {
        throw new Error('poster-generate-failed')
      }
      const downloadUrl = URL.createObjectURL(blob)
      const fileName = sanitizeFileName(props.project.projectName || 'adeff-share')
      const anchor = document.createElement('a')
      anchor.href = downloadUrl
      anchor.download = `${fileName}.png`
      anchor.click()
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000)
    } catch (error) {
      console.error(error)
      message.error(t('dashboard.projectDetailPage.shareFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleCopyText = () => {
    const success = copy(shareText)
    if (success) {
      message.success(t('dashboard.projectDetailPage.shareModal.copySuccess'))
      return
    }
    message.error(t('dashboard.projectDetailPage.shareModal.copyFailed'))
  }

  return (
    <AModal
      testId='share-poster-modal'
      open={modal.visible}
      title={t('dashboard.projectDetailPage.shareModal.title')}
      className={styles.sharePosterModal}
      width={731}
      footer={null}
      onCancel={() => modal.hide()}
      afterClose={() => modal.remove()}
    >
      <div className={styles.content}>
        <div className={styles.posterPreview} ref={previewPosterRef}>
          <ProjectSharePoster
            project={props.project}
            userName={props.userName}
            userAvatar={props.userAvatar}
            downloadedAt={props.downloadedAt}
            percentile={percentile}
          />
        </div>
        <div className={styles.rightPanel}>
          <p className={styles.desc}>{t('dashboard.projectDetailPage.shareModal.desc')}</p>
          <div className={styles.textBox}>
            {shareText}
          </div>
          <div className={styles.actions}>
            <AButton testId='share-poster-copy-text-btn' size='LG' type='outlined' onClick={handleCopyText} style={{ width: '100%' }}>
              {t('dashboard.projectDetailPage.shareModal.copyText')}
            </AButton>
            <AButton testId='share-poster-save-image-btn' size='LG' type='primary' loading={saving} onClick={handleSaveImage} style={{ width: '100%' }}>
              {t('dashboard.projectDetailPage.shareModal.saveImage')}
            </AButton>
          </div>
        </div>
      </div>
    </AModal>
  )
}

export default NiceModal.create(SharePosterModal)
